import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Grid } from '@mui/material'
import { Edit } from '@mui/icons-material'

import {
  useDELETE_parentMutation,
  useGET_parentByIdQuery,
  usePOST_parentMutation,
  usePUT_parentMutation,
} from 'redux/api/parentsApi'
import { useGET_studentsProfileSelectQuery } from 'redux/api/studentsApi'

import { Form, Formik } from 'formik'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import DatePickerField from 'components/atoms/fields/DatePickerField'
import InputField from 'components/atoms/fields/InputField'
import SelectField from 'components/atoms/fields/SelectField'

import useCrudData from 'utils/customHooks/useCrudData'
import formModel, { initialValues, validationSchema } from 'utils/formModels/parentModel'
import { crudColorMap, crudIconMap, emergencyContactTypeSelectData, genderSelectData, maritalStateSelectData } from 'utils/helpers'

import { ActionType, IParent } from 'types'
import SingleAttachmentButton from 'components/atoms/buttons/SingleAttachmentButton'
import { useDELETE_profilePictureMutation, usePUT_profilePictureMutation } from 'redux/api/usersApi'
import { useErrorAlert } from 'utils/customHooks/useErrorAlert'
import SectionTitle from 'components/atoms/SectionTitle'
import { useSelector } from 'react-redux'
import { RootState } from 'redux/store'

interface IProps {
  action: ActionType
  id: string
  viewOnly: boolean
}

const ParentForm: FC<IProps> = ({ action, id, viewOnly }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const backToSlug = '/parents'
  const editSlug = `/parents/edit/${id}`

  const user = useSelector((state: RootState) => state['user'].user)
  
  const [formSubmitCount, setFormSubmitCount] = useState(0)
  const onSuccess = () => {
    setFormSubmitCount(formSubmitCount+1)
  }

  const {
    email,
    first_name,
    mid_name,
    last_name,
    gender,
    birthday,
    phone_number,
    mobile_number,
    address,
    city,
    district,
    state,
    zip,
    marital_state,
    marital_zip,
    emergency_contact_name,
    emergency_contact_phone,
    emergency_contact_email,
    emergency_contact_relationship,
    emergency_contact_address,
    emergency_contact_city,
    emergency_contact_district,
    emergency_contact_state,
    emergency_contact_zip,
    school_academic_id,
    tax_id,
    children,
    picture
  } = formModel()

  const { isLoading, entity, handleSubmit } = useCrudData({
    backToSlug,
    onSuccess,
    entityName: `${t('common.parent')}`,
    entityId: id,
    getEntityQuery: useGET_parentByIdQuery,
    createEntityMutation: usePOST_parentMutation,
    editEntityMutation: usePUT_parentMutation,
    deleteEntityMutation: useDELETE_parentMutation,
  })

  const [PUT_profilePicture, { error: profilePictureSaveError }] = usePUT_profilePictureMutation()
  const [DELETE_profilePicture, { error: profilePictureDeleteError }] = useDELETE_profilePictureMutation()

  useErrorAlert(profilePictureSaveError)
  useErrorAlert(profilePictureDeleteError)


  const serverValues = entity?.id
    ? {
        ...initialValues,
        ...entity,
        role_id: entity.role,
        gender: genderSelectData().data.list.find((r) => r.id === entity?.gender),
        marital_state: maritalStateSelectData().data.list.find((r) => r.id === entity?.marital_state) || null,
        emergency_contact_relationship: emergencyContactTypeSelectData().data.list.find((r) => r.id === entity?.emergency_contact_relationship)  || null,
        picture: entity.profile_picture
      }
    : initialValues

  return (
    <>
      { serverValues &&  serverValues?.id &&
            <Box className="thick-border" sx={{ display: 'inline-block', background: '#fff', marginBottom: '40px', width: 'auto', maxWidth: '840px' }}>
              <div style={{ display: 'flex', alignItems: 'start' }}>

                {
                  serverValues?.profile_picture &&

                  <div style={{ width: '240px', paddingRight: '40px' }}>
                    <div style={{ 
                      width: '240px', 
                      maxWidth: '100%', 
                      aspectRatio: '1 / 1',
                      borderRadius: '50%', 
                      backgroundSize: 'cover',
                      backgroundPosition: 'center center',
                      backgroundImage: 'url('+serverValues?.profile_picture+')'
                    }} />
                  </div>
                }
                
                <div style={{ alignSelf: 'start' }}>
                    <div>{t('common.name')}: <b>{ serverValues.first_name } { serverValues.mid_name } { serverValues.last_name }</b></div>
                    <div style={{ marginTop: '10px' }}>{serverValues.children && <div>{t('common.children')}: <b>{ serverValues.children.map(p => p.title ).join(', ') }</b></div>}</div>
                    <div style={{ marginTop: '10px' }}>{t('common.email')}: <b>{ serverValues.email }</b></div>
                    <div style={{ marginTop: '10px' }}>{t('common.phone')}: <b>{ serverValues.phone_number }</b></div>
                    <div style={{ marginTop: '10px' }}><b>{ serverValues.state }, { serverValues.city }, { serverValues.zip }</b></div>   
                    <div style={{ marginTop: '10px' }}><b>{ serverValues.address }</b></div>    
                </div>

                <div style={{ paddingLeft: '20px', alignSelf: 'start' }}>
                    <div>{t('common.emergency-contact')}: <b>{ serverValues.emergency_contact_name }</b></div> 
                    <div style={{ marginTop: '10px' }}>{t('common.relationship')}: <b>{ serverValues.emergency_contact_relationship?.title }</b></div>  
                    <div style={{ marginTop: '10px' }}>{t('common.email')}: <b>{ serverValues.emergency_contact_email }</b></div>
                    <div style={{ marginTop: '10px' }}>{t('common.phone')}: <b>{ serverValues.emergency_contact_phone }</b></div>
                    <div style={{ marginTop: '10px' }}><b>{ serverValues.emergency_contact_state }, { serverValues.emergency_contact_city }, { serverValues.emergency_contact_zip }</b></div>    
                    <div style={{ marginTop: '10px' }}><b>{ serverValues.emergency_contact_address }</b></div>    
                </div>


              </div> 
            </Box>
          }
          
      <Formik
        enableReinitialize
        initialValues={serverValues}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          const data: IParent = {
            ...values,
            gender: values?.gender?.id ?? '',
            marital_state: values?.marital_state?.id  ?? '',
            emergency_contact_relationship: values?.emergency_contact_relationship?.id  ?? '',
            children_ids: values.children.map((child) => child.id),
          }
          handleSubmit(action, id, data)
        }}
      >{
        ({ values, setFieldValue, errors }) => 
        <Form>
          <Grid container spacing={4} sx={{ marginTop: '-1rem' }}>

            <Grid item xs={12} md={12}>
              <SectionTitle text={`${t('common.personal-details')}`} />
            </Grid>  


            <Grid item xs={12} md={6} lg={4}>
              <InputField fieldProps={{ ...school_academic_id }} disabled={viewOnly} />
            </Grid>

            <Grid item xs={12} md={6} lg={4}>
              <InputField fieldProps={{ ...tax_id }} disabled={viewOnly} />
            </Grid>

            <Grid item xs={12} md={12} lg={4}>
              <SelectField
                multiple={true}
                fieldProps={{ ...children }}
                apiCall={useGET_studentsProfileSelectQuery}
                disabled={viewOnly}
              />
            </Grid>


            <Grid item xs={12} md={4}>
              <InputField fieldProps={{ ...first_name }} disabled={viewOnly} />
            </Grid>
            <Grid item xs={12} md={4}>
              <InputField fieldProps={{ ...mid_name }} disabled={viewOnly} />
            </Grid>
            <Grid item xs={12} md={4}>
              <InputField fieldProps={{ ...last_name }} disabled={viewOnly} />
            </Grid>


            <Grid item xs={12} md={4} lg={2}>
              <DatePickerField fieldProps={{ ...birthday }} disabled={viewOnly} maxDate={new Date()} />
            </Grid>
            <Grid item xs={12} md={4} lg={2}>
              <SelectField
                renderChildren={false}
                fieldProps={{ ...gender }}
                apiCall={genderSelectData}
                disabled={viewOnly}
              />
            </Grid>
            <Grid item xs={12} md={4} lg={4}>
              <InputField fieldProps={{ ...email }} disabled={viewOnly} />
            </Grid>
            <Grid item xs={12} md={6} lg={4}>
              <InputField fieldProps={{ ...mobile_number }} disabled={viewOnly} />
            </Grid>


            <Grid item xs={12} md={12}>
              <SectionTitle text={`${t('common.home-address')}`} />
            </Grid>  

            <Grid item xs={12}>
              <InputField fieldProps={{ ...address }} disabled={viewOnly} />
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
              <InputField fieldProps={{ ...city }} disabled={viewOnly} />
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
              <InputField fieldProps={{ ...district }} disabled={viewOnly} />
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
              <InputField fieldProps={{ ...state }} disabled={viewOnly} />
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
              <InputField fieldProps={{ ...zip }} disabled={viewOnly} />
            </Grid>


            <Grid item xs={12} md={12}>
              <SectionTitle text={`${t('common.marital-state')}`} />
            </Grid>  

            <Grid item xs={12} md={6}>
              <SelectField
                renderChildren={false}
                fieldProps={{ ...marital_state }}
                apiCall={maritalStateSelectData}
                disabled={viewOnly}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InputField fieldProps={{ ...marital_zip }} disabled={viewOnly} />
            </Grid>


            <Grid item xs={12} md={12}>
              <SectionTitle text={`${t('common.emergency-contact')}`} />
            </Grid>  

            <Grid item xs={12} md={6}>
              <SelectField
                renderChildren={false}
                fieldProps={{ ...emergency_contact_relationship }}
                apiCall={emergencyContactTypeSelectData}
                disabled={viewOnly}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <InputField fieldProps={{ ...emergency_contact_name }} disabled={viewOnly} />
            </Grid>

            <Grid item xs={12} md={6}>
              <InputField fieldProps={{ ...emergency_contact_email }} disabled={viewOnly} />
            </Grid>
            <Grid item xs={12} md={6}>
              <InputField fieldProps={{ ...emergency_contact_phone }} disabled={viewOnly} />
            </Grid>

            <Grid item xs={12}>
              <InputField fieldProps={{ ...emergency_contact_address }} disabled={viewOnly} />
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
              <InputField fieldProps={{ ...emergency_contact_city }} disabled={viewOnly} />
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
              <InputField fieldProps={{ ...emergency_contact_district }} disabled={viewOnly} />
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
              <InputField fieldProps={{ ...emergency_contact_state }} disabled={viewOnly} />
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
              <InputField fieldProps={{ ...emergency_contact_zip }} disabled={viewOnly} />
            </Grid>



            {serverValues?.id &&
            <Grid item xs={12}>
                <SingleAttachmentButton
                  fieldProps={picture}
                  values={values}
                  errors={errors}
                  setFieldValue={setFieldValue}
                  id={serverValues?.id}
                  label={t('common.profile-picture')}
                  formSubmitCount={formSubmitCount}
                  savedAttachmentKey={'picture'}
                  saveAttachmentApiCall={PUT_profilePicture}
                  deleteAttachmentApiCall={DELETE_profilePicture}
                />
              </Grid>
            }


            <Grid item xs={12}>
              <Box sx={{ display: 'flex' }} className="transparent-background">
                <Button
                  variant='outlined'
                  color='black'
                  onClick={() => navigate(backToSlug)}
                  sx={{ minHeight: '2.5rem', width: '10rem' }}
                >
                  <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                  {`${t('common.back')}`}
                </Button>
                {action === 'view' && ['principal','staff-administrator'].includes(user?.role) && 
                  <Box sx={{ marginLeft: 'auto' }}>
                    <Button
                      variant='outlined'
                      color='black'
                      onClick={() => navigate(editSlug)}
                      sx={{ minHeight: '2.5rem', width: '10rem' }}
                    >
                      <Edit style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                      {`${t('common.edit')}`}
                    </Button>
                  </Box>
                }
                {action !== 'view' && (
                  <SubmitButton
                    color={crudColorMap[action]}
                    text={`${t('form-actions.' + action)}`}
                    icon={crudIconMap[action]}
                    sx={{ width: '10rem' }}
                    isLoading={isLoading}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </Form>
        }
      </Formik>

    </>
  )
}
export default ParentForm
